import type { ProbeSource } from "./probe_types";

import { is_record, read_string } from "./validation";

/**
 * Metadata encoded into the probe page URL.
 */
export interface ProbePageMetadata {
  /** Browserling browser key such as `chrome/138`, when known. */
  readonly browser_key: string | null;
  /** Launcher that opened the probe page. */
  readonly launcher: ProbeSource;
  /** Browserling platform key such as `win/10`, when known. */
  readonly platform_key: string | null;
  /** Stable probe run identifier used for polling/export. */
  readonly probe_id: string;
}

const META_QUERY_PARAM = "meta";
const UTF8_DECODER = new TextDecoder();
const UTF8_ENCODER = new TextEncoder();

/**
 * Builds the canonical `/check` URL for a probe page run.
 */
export function build_probe_page_url(
  origin: string,
  metadata: ProbePageMetadata
): string {
  const normalized_origin = origin.endsWith("/")
    ? origin.slice(0, -1)
    : origin;
  const url = new URL("/check", normalized_origin);
  url.searchParams.set(META_QUERY_PARAM, encode_probe_page_metadata(metadata));
  return url.toString();
}

/**
 * Encodes probe page metadata as base64url JSON.
 */
export function encode_probe_page_metadata(
  metadata: ProbePageMetadata
): string {
  const payload: Record<string, string> = {
    launcher: metadata.launcher,
    probe_id: metadata.probe_id
  };

  if (metadata.platform_key !== null) {
    payload["platform_key"] = metadata.platform_key;
  }

  if (metadata.browser_key !== null) {
    payload["browser_key"] = metadata.browser_key;
  }

  return encode_base64url(JSON.stringify(payload));
}

/**
 * Decodes a base64url metadata payload from the page URL.
 */
export function decode_probe_page_metadata(
  encoded_metadata: string
): ProbePageMetadata {
  const decoded_text = decode_base64url(encoded_metadata);
  const parsed: unknown = JSON.parse(decoded_text);
  return parse_probe_page_metadata(parsed);
}

/**
 * Reads metadata from the current probe page URL.
 */
export function read_probe_page_metadata(
  current_url: URL
): ProbePageMetadata | null {
  const encoded_metadata = current_url.searchParams.get(META_QUERY_PARAM);
  if (encoded_metadata === null || encoded_metadata.length === 0) {
    return null;
  }

  try {
    return decode_probe_page_metadata(encoded_metadata);
  } catch {
    return null;
  }
}

function parse_probe_page_metadata(value: unknown): ProbePageMetadata {
  if (!is_record(value)) {
    throw new Error("probe page metadata is not an object");
  }

  const probe_id = read_string(value, "probe_id");
  const launcher = read_probe_launcher(value);
  const platform_key = read_nullable_string(value, "platform_key");
  const browser_key = read_nullable_string(value, "browser_key");

  if (probe_id === null || probe_id.length === 0) {
    throw new Error("probe page metadata is missing probe_id");
  }

  if (launcher === null) {
    throw new Error("probe page metadata is missing launcher");
  }

  return {
    browser_key,
    launcher,
    platform_key,
    probe_id
  };
}

function read_probe_launcher(
  record: Record<string, unknown>
): ProbeSource | null {
  const raw_launcher = read_string(record, "launcher") ?? read_string(record, "source");

  switch (raw_launcher) {
    case "browserling":
    case "local_script":
    case "manual":
      return raw_launcher;
    case null:
      return null;
    default:
      return null;
  }
}

function read_nullable_string(
  record: Record<string, unknown>,
  key: string
): string | null {
  const value = record[key];
  return typeof value === "string" ? value : null;
}

function encode_base64url(value: string): string {
  const bytes = UTF8_ENCODER.encode(value);
  let binary = "";

  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }

  return btoa(binary)
    .replaceAll("+", "-")
    .replaceAll("/", "_")
    .replace(/=+$/u, "");
}

function decode_base64url(value: string): string {
  const padded_value =
    value.length % 4 === 0
      ? value
      : `${value}${"=".repeat(4 - (value.length % 4))}`;
  const binary = atob(
    padded_value.replaceAll("-", "+").replaceAll("_", "/")
  );
  const bytes = Uint8Array.from(
    binary,
    (entry: string): number => entry.charCodeAt(0)
  );

  return UTF8_DECODER.decode(bytes);
}
